import { useEffect } from 'react';

import {
  classifyCookieSignal,
  fetchTollhouseConsent,
  MAX_POLL_ATTEMPTS,
  POLL_INTERVAL_MS,
  reconcile,
  setConsentViaApi,
  writeTollhouseConsent
} from 'hooks/useTrustArcConsent';

interface UseConsentReconciliationOptions {
  accountConfig?: { url: string };
  isAuthenticated?: boolean;
}

/**
 * App-level consent reconciliation, run from the app shell so it fires on the first
 * authenticated page load — not only when the user opens the Privacy Data Sharing page.
 * Without it, a customer with a durable Tollhouse opt-out signing in on a fresh browser
 * leaks ad pixels all session: the anonymous TrustArc cookie starts as implied,us (ads
 * allowed) and nothing squares it against the durable record. Reuses the same reconcile()
 * engine as useTrustArcConsent's EFFECT A.
 *
 * Writes only — no display/radios/prefclose. Callers must exclude the Privacy route so that
 * page's hook owns reconciliation there; that also keeps any prefclose patch off, so the raw
 * setConsentViaApi write is safe.
 */
const useConsentReconciliation = (options: UseConsentReconciliationOptions = {}): void => {
  const { accountConfig, isAuthenticated = false } = options;
  // Key off the URL string, not the accountConfig object, so a fresh reference from the
  // memoized selector can't re-run reconciliation.
  const accountUrl = accountConfig?.url;

  useEffect(() => {
    if (!isAuthenticated || !accountUrl) {
      return;
    }

    const config = { url: accountUrl };
    let cancelled = false;
    let cmaPollInterval: ReturnType<typeof setInterval> | null = null;

    // setConsentViaApi silently no-ops until CMA loads (it hasn't at login), so poll until
    // it appears, then write once.
    const writeCookieRejectWhenCmaReady = () => {
      if (window.truste?.cma) {
        setConsentViaApi('REJECT_ALL');
        return;
      }

      let attempts = 0;
      cmaPollInterval = setInterval(() => {
        if (cancelled) {
          if (cmaPollInterval) {
            clearInterval(cmaPollInterval);
          }
          return;
        }

        attempts += 1;

        if (window.truste?.cma) {
          setConsentViaApi('REJECT_ALL');
          if (cmaPollInterval) {
            clearInterval(cmaPollInterval);
          }
          return;
        }

        if (attempts >= MAX_POLL_ATTEMPTS && cmaPollInterval) {
          clearInterval(cmaPollInterval);
        }
      }, POLL_INTERVAL_MS);
    };

    const runReconciliation = async () => {
      const signal = classifyCookieSignal();

      // reconcile() is NONE for both; skip the Tollhouse fetch.
      if (signal === 'EU' || signal === 'NO_SIGNAL') {
        return;
      }

      const tollhouseState = await fetchTollhouseConsent(config);
      // Tollhouse unavailable: leave the cookie as-is; the next page load retries.
      if (cancelled || tollhouseState === null) {
        return;
      }

      const { action } = reconcile(signal, tollhouseState);

      if (action === 'WRITE_OPT_OUT') {
        writeTollhouseConsent(config, 'REJECT_ALL');
      } else if (action === 'WRITE_OPT_IN') {
        writeTollhouseConsent(config, 'ACCEPT_ALL');
      } else if (action === 'SYNC_COOKIE_REJECT') {
        writeCookieRejectWhenCmaReady();
      }
    };

    runReconciliation();

    return () => {
      cancelled = true;
      if (cmaPollInterval) {
        clearInterval(cmaPollInterval);
      }
    };
  }, [accountUrl, isAuthenticated]);
};

export default useConsentReconciliation;
